import { Bullet, Circle, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  DateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { otherValues } from "@/settings/constants";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class LineSeriesChart extends BaseChart {
  declare chart: XYChart;
  declare renderers: {
    x: AxisRendererX;
    y: AxisRendererY;
  };
  interval!: time.ITimeInterval;

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      this.chart.series.clear();
    }

    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
        maxTooltipDistance: -1,
      })
    );

    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = new Set<string>();
    this.data.forEach((el: ChartDataRecord) => {
      series.add(el[this.settings.decompositionMetric] as string);
    });
    const seriesNames = Array.from(series).map((name: string) => {
      return name === null ? Lang.null : name;
    });

    seriesNames.forEach((name, index) => {
      if (typeof this.createSeries === "function") {
        return this.createSeries(name, index);
      }
    });

    this.showLegend(seriesNames, this.chart.series.values as LegendData);
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 60 });
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const y = AxisRendererY.new(this.root, {});

    y.grid.template.setAll({
      strokeWidth: 1,
      strokeDasharray: [10, 5],
    });

    y.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.renderers = { x, y };
  }

  protected createAxes() {
    const x = this.chart.xAxes.push(
      DateAxis.new(this.root, {
        renderer: this.renderers.x,
        groupData: true,
        groupIntervals: [this.interval],
        markUnitChange: false,
        baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
        dateFormats: getAxisDateFormat(this.settings.xAxis),
      })
    );

    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          paddingBottom: 0,
          centerX: p50,
        })
      );
    }

    const y = this.chart.yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.y,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxis, this.data),
      })
    );

    if (this.settings.showDataLabels) {
      const currentExtraMax = Number(y.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (currentExtraMax < requiredExtraMax) {
        y.set("extraMax", requiredExtraMax);
      }
    }

    const yAxisTitle = this.settings.yAxisTitle;
    if (yAxisTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, y };
  }

  protected createCursor() {
    const cursor = this.chart.set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.x,
      })
    );

    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", false);
  }

  protected createSeries(seriesName: string, index: number) {
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series = this.chart.series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        fill: this.getSeriesColor(index),
        minBulletDistance: 10,
        valueYField: this.settings.yAxis,
        valueXField: this.settings.xAxis,
      })
    ) as LineSeries;

    if (this.options.showBullets) {
      series.bullets.push(() => {
        return Bullet.new(this.root, {
          sprite: Circle.new(this.root, {
            radius: 4,
            fill: series.get("fill"),
          }),
        });
      });
    }

    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = this.settings.yAxis;
    const metricFormatter = this.formatters?.[metricKey];
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        ...chartFontSettings.label,
        centerY: p50,
        dy: -16,
        dx: -32,
        populateText: true,
        fontSize: labelFontSize,
        text: "{valueY}",
      });

      label.adapters.add("text", (labelText, target) => {
        const rawValue = Number(target?.dataItem?.get("valueY" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return "";
        }

        if (!this.settings.showDataLabels) {
          return "";
        }

        return metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));
      });

      label.adapters.add("visible", (visible) => {
        return this.settings.showDataLabels ? visible : false;
      });

      return Bullet.new(this.root, {
        locationY: 1,
        sprite: label,
      });
    });

    this.createTooltip(series, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });

    series.set("stroke", this.getSeriesColor(index));
    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    this.createLegendLabel(series, seriesName);
    this.updateSeriesData(seriesName, series);
  }

  protected mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);
    if (
      this.module === PLAN_MODULE &&
      !this.settings?.useExploreData &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.decompositionMetric;
      const displayMetric = this.settings.yAxis;

      const groupedPreparedDataMap = preparedData.reduce<Record<string, number>>((acc, item) => {
        const recordItem = item as ChartDataRecord;
        const groupKey = recordItem[decompositionMetric] as string;
        const currentValue = Number(recordItem[displayMetric] ?? 0);

        acc[groupKey] = (acc[groupKey] || 0) + currentValue;
        return acc;
      }, {});

      const groupedPreparedData = Object.entries(groupedPreparedDataMap).map(([key, total]) => ({ key, total }));
      const sortedGroupedPreparedData = groupedPreparedData.sort((a, b) => b.total - a.total);
      const selectedGroupedPreparedData = sortedGroupedPreparedData.slice(0, Number(this.settings.showItems));
      const selectedSecondDimensions = selectedGroupedPreparedData.map((item) => item.key);

      preparedData = preparedData.filter((item) =>
        selectedSecondDimensions.includes((item as ChartDataRecord)[decompositionMetric] as string)
      );
    }

    return preparedData.map((el) => {
      return otherValues.includes(el[this.settings.decompositionMetric] as string)
        ? { ...el, [this.settings.decompositionMetric]: Lang.other }
        : el;
    });
  }

  protected updateSeriesData(seriesName: string, series: LineSeries): void {
    const data = [] as ChartDataRecord[];
    this.data.forEach((el: ChartDataRecord) => {
      const date = stringOrNumberToDate(el[this.settings.xAxis] as string).getTime();
      data.push({
        [this.settings.xAxis]: date,
        [this.settings.yAxis]: el[this.settings.yAxis],
        [this.settings.decompositionMetric]:
          el[this.settings.decompositionMetric] === null ? Lang.null : el[this.settings.decompositionMetric],
      });
    });

    const seriesData = data.filter((el) => {
      return el[this.settings.decompositionMetric] === seriesName;
    });

    seriesData.sort((a, b) => {
      return (a[this.settings.xAxis] as number) - (b[this.settings.xAxis] as number);
    });

    series.data.setAll(seriesData);
  }
}
